import { isEmpty } from 'lodash'

import { sentryLogger } from 'sentry'

import {
  apiEndpoints,
  validateDeleteRequest,
  validateUpdateRequest
} from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, patch, post, remove } from '../request'

const subjectRequestStructure = (data) => ({
  name: data.name,
  internal_name: data?.internal_name,
  subject_category: data.subject_category,
  description_title: data.description_title,
  description: data.description,
  image: data.image,
  app_image: data.app_image,
  progress_enabled: data?.progress_enabled,
  discovery_enabled: data?.discovery_enabled,
  subject_ratings: [
    {
      rating: 1,
      media_url:
        data?.subject_ratings?.[2]?.media_url &&
        data?.subject_ratings?.[2]?.media_url !== ''
          ? data?.subject_ratings?.[2]?.media_url
          : null
    },
    {
      rating: 2,
      media_url:
        data?.subject_ratings?.[1]?.media_url &&
        data?.subject_ratings?.[1]?.media_url !== ''
          ? data?.subject_ratings?.[1]?.media_url
          : null
    },
    {
      rating: 3,
      media_url:
        data?.subject_ratings?.[0]?.media_url &&
        data?.subject_ratings?.[0]?.media_url !== ''
          ? data?.subject_ratings?.[0]?.media_url
          : null
    }
  ],
  centres: data?.centres,
  standard_group: data?.standard_group,
  coins_per_month: data?.coins_per_month,
  ...(data?.subject_media && {
    subject_media: data?.subject_media?.map((mediaData, index) => {
      return {
        caption_title: !isEmpty(mediaData?.caption_title)
          ? mediaData?.caption_title
          : null,
        caption: !isEmpty(mediaData?.caption) ? mediaData?.caption : null,
        file: mediaData?.file?.uuid,
        sequence: index + 1
      }
    })
  }),
  ...(data?.carousel_images && {
    carousel_images: data.carousel_images?.map((mediaData, index) => {
      return {
        file: mediaData?.file?.uuid,
        sequence: index + 1
      }
    })
  }),
  ...(data?.activities && {
    activities: data.activities?.map((mediaData, index) => {
      return {
        title: !isEmpty(mediaData?.title) ? mediaData?.title : null,
        description: !isEmpty(mediaData?.description)
          ? mediaData?.description
          : null,
        file: mediaData?.file?.uuid,
        sequence: index + 1
      }
    })
  }),
  ...(data?.important_notes && { important_notes: data?.important_notes }),
  certifications: data?.certifications
})

const fetchSubject = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.subject, {
        ...queries,
        exclude_subject_category_name: 'Playdate,Toddler Club'
      })

  try {
    const response = await get(requestUrl)

    const result = {
      ...response.data,
      results: response.data.results?.map((subject) => {
        return {
          ...subject,
          subject_media: subject.subject_media
            ?.map((media) => {
              return {
                caption_title: media?.file?.caption_title,
                caption: media?.file?.caption,
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence)
        }
      })
    }

    return result
  } catch (error) {
    sentryLogger(error, 'error in fetching subject')
    throw error
  }
}

const fetchAllSubject = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.subject, {
        ...queries
      })

  try {
    const response = await get(requestUrl)

    const result = {
      ...response.data,
      results: response.data.results?.map((subject) => {
        return {
          ...subject,
          subject_media: subject.subject_media
            ?.map((media) => {
              return {
                caption_title: media?.file?.caption_title,
                caption: media?.file?.caption,
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence)
        }
      })
    }

    return result
  } catch (error) {
    sentryLogger(error, 'error in fetching subject')
    throw error
  }
}

const fetchPlaydateSubject = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.subject, {
        ...queries,
        subject_category_name: 'playdate'
      })

  try {
    const response = await get(requestUrl)

    const result = {
      ...response.data,
      results: response.data.results?.map((subject) => {
        return {
          ...subject,
          subject_media: subject.subject_media
            ?.filter?.((media) => media?.type === 'general')
            ?.map((media) => {
              return {
                caption_title: media?.file?.caption_title,
                caption: media?.file?.caption,
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence),
          carousel_images: subject.subject_media
            ?.filter?.((media) => media?.type === 'carousel')
            ?.map((media) => {
              return {
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence),
          activities: subject.subject_media
            ?.filter?.((media) => media?.type === 'activity')
            ?.map((media) => {
              return {
                title: media?.file?.caption_title,
                description: media?.file?.caption,
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence)
        }
      })
    }

    return result
  } catch (error) {
    sentryLogger(error, 'error in fetching subject')
    throw error
  }
}

const fetchToddlerClubSubject = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.subject, {
        ...queries,
        subject_category_name: 'toddler club'
      })

  try {
    const response = await get(requestUrl)

    const result = {
      ...response.data,
      results: response.data.results?.map((subject) => {
        return {
          ...subject,
          subject_media: subject.subject_media
            ?.filter?.((media) => media?.type === 'general')
            ?.map((media) => {
              return {
                caption_title: media?.file?.caption_title,
                caption: media?.file?.caption,
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence),
          carousel_images: subject.subject_media
            ?.filter?.((media) => media?.type === 'carousel')
            ?.map((media) => {
              return {
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence),
          activities: subject.subject_media
            ?.filter?.((media) => media?.type === 'activity')
            ?.map((media) => {
              return {
                title: media?.file?.caption_title,
                description: media?.file?.caption,
                file: media?.file,
                sequence: media?.sequence
              }
            })
            .sort((a, b) => a.sequence - b.sequence)
        }
      })
    }

    return result
  } catch (error) {
    sentryLogger(error, 'error in fetching subject')
    throw error
  }
}
const validateSubjectPayload = (data) => {
  let payload = data
  let mediaValidation = payload.subject_media?.filter((val) => val.file)
  if (mediaValidation?.length) {
    payload.subject_media = mediaValidation
  } else {
    payload.subject_media = null
  }

  return payload
}

const createPlaydateSubject = async (data) => {
  return await createSubject({
    ...data,
    // subject_category: 'fdff1522-a7d0-4709-8f17-68b6a64a2501', //dev
    // subject_category: '81adb839-eb8c-4249-b729-ba72ee79ab6a' //staging,
    subject_category: 'adfca648-844d-4ef6-ad06-6bc82aac991a'
  })
}

const createSubject = async (data) => {
  const payload = validateSubjectPayload(subjectRequestStructure(data))
  try {
    const response = await post(apiEndpoints.subject, payload)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating subject')
    throw error
  }
}

const updateSubject = async (data) => {
  validateUpdateRequest(data)

  const payload = validateSubjectPayload(subjectRequestStructure(data))
  try {
    const response = await patch(
      `${apiEndpoints.subject}/${data.uuid}/`,
      payload
    )

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating subject')
    throw error
  }
}

const updatePlaydateSubject = async (data) => {
  return await updateSubject({
    ...data,
    // subject_category: 'fdff1522-a7d0-4709-8f17-68b6a64a2501', //dev
    // subject_category: '81adb839-eb8c-4249-b729-ba72ee79ab6a' //staging,
    subject_category: 'adfca648-844d-4ef6-ad06-6bc82aac991a'
  })
}

const createToddlerClubSubject = async (data) => {
  return await createSubject({
    ...data,
    // subject_category: 'fdff1522-a7d0-4709-8f17-68b6a64a2501', //dev
    // subject_category: '81adb839-eb8c-4249-b729-ba72ee79ab6a' //staging,
    subject_category: 'c0ffac01-ec3a-4bbf-a55c-96da81879144'
  })
}

const updateToddlerClubSubject = async (data) => {
  return await updateSubject({
    ...data,
    // subject_category: 'fdff1522-a7d0-4709-8f17-68b6a64a2501', //dev
    // subject_category: '81adb839-eb8c-4249-b729-ba72ee79ab6a' //staging,
    subject_category: 'c0ffac01-ec3a-4bbf-a55c-96da81879144'
  })
}

const deleteSubject = async ({ ids }) => {
  validateDeleteRequest(ids)

  try {
    await Promise.all(
      ids.map((id) => {
        return remove(`${apiEndpoints.subject}/${id}`)
      })
    )

    return ids
  } catch (error) {
    sentryLogger(error, 'error in deleting subject')
    throw error
  }
}

export {
  fetchSubject,
  fetchPlaydateSubject,
  fetchToddlerClubSubject,
  createSubject,
  createPlaydateSubject,
  createToddlerClubSubject,
  updateSubject,
  updatePlaydateSubject,
  updateToddlerClubSubject,
  deleteSubject,
  fetchAllSubject
}
